import React from 'react'
import { i18n } from '../index'
import get from 'lodash/get'
import moment from 'moment'
import type { BudgetPlanItem, ForecastSalesItem, PlanItem } from './types'
import classnames from 'classnames'
import WeekRow from './WeekRow'
import DayRow from './DayRow'
import sumBy from 'lodash/sumBy'
import css from './BudgetTable.css'

const getEfficiencyFactor = (efficiencyFactors, selectedOptionId, currentMonth): number => {
  const efficiencyFactor = efficiencyFactors.find((item) => item.id === selectedOptionId) || {}
  const efficiencyFactorForMonth =
    get(efficiencyFactor, 'efficiencyFactors', []).find((item) => item.month === currentMonth) || {}
  return efficiencyFactorForMonth.efficiencyFactor || 0
}

const DayRows = (
  dailyBudgetData,
  dailyForecastData,
  dailyPlanData,
  onAmountChange,
  editable,
  targetEfficiency,
  isStationPage,
  stationId,
  onNoteDialogOpen,
) =>
  dailyBudgetData.map((budget, index) => (
    <React.Fragment key={budget.date}>
      <DayRow
        budget={budget}
        forecast={dailyForecastData[index]}
        plan={dailyPlanData[index]}
        onAmountChange={onAmountChange}
        editable={editable}
        isStationPage={isStationPage}
        stationId={stationId}
        onNoteDialogOpen={onNoteDialogOpen}
      />
      {moment(budget.date).day() === 0 && (
        <WeekRow
          week={moment(budget.date).week()}
          budgetData={dailyBudgetData}
          forecastData={dailyForecastData}
          planData={dailyPlanData}
          targetEfficiency={targetEfficiency}
          isStationPage={isStationPage}
        />
      )}
    </React.Fragment>
  ))

type Props = {
  dailyBudgetData: Array<BudgetPlanItem>
  dailyForecastData: Array<ForecastSalesItem>
  dailyPlanData: Array<PlanItem>
  editable: boolean
  isStationPage: boolean
  onAmountChange: (...args: Array<any>) => any
  stationId: string
  selectedOptionId: string
  startDate: string
  efficiencyFactors: any
  onNoteDialogOpen: (...args: Array<any>) => any
}

const BudgetTable = ({
  dailyBudgetData,
  dailyForecastData,
  dailyPlanData,
  editable,
  isStationPage,
  onAmountChange,
  stationId,
  selectedOptionId,
  startDate,
  efficiencyFactors,
  onNoteDialogOpen,
}: Props) => {
  const budgetedSales = sumBy(dailyBudgetData, 'budgetedSales')
  const budgetedHours = sumBy(dailyBudgetData, 'budgetedHours')
  const totalForecastSales = sumBy(dailyForecastData, 'sales')
  const totalPlanSales = sumBy(dailyPlanData, 'plannedSales')
  const totalPlanHours = sumBy(dailyPlanData, 'plannedHours')
  const efficiency = totalPlanHours ? totalPlanSales / totalPlanHours : 0
  const currentMonth = new Date(startDate).getMonth() + 1
  const targetEfficiency = getEfficiencyFactor(efficiencyFactors, selectedOptionId, currentMonth)
  return (
    <table className={css.budgetTable}>
      <thead>
        <tr>
          <th />
          <th>{i18n.t('budgetPlanning.table.budgetedSales')}</th>
          <th>{i18n.t('budgetPlanning.table.budgetedHours')}</th>
          {isStationPage && <th>{i18n.t('budgetPlanning.table.forecastSales')}</th>}
          <th>{i18n.t('budgetPlanning.table.plannedSales')}</th>
          <th>{i18n.t('budgetPlanning.table.plannedHours')}</th>
          <th>{i18n.t('budgetPlanning.table.efficiency')}</th>
          <th>{i18n.t('budgetPlanning.table.note')}</th>
        </tr>
      </thead>
      <tbody>
        <tr className={css.monthRow}>
          <td>{i18n.t('budgetPlanning.table.wholeMonth')}</td>
          <td>{budgetedSales.toFixed(0)}</td>
          <td>{budgetedHours.toFixed(0)}</td>
          {isStationPage && <td>{totalForecastSales.toFixed(0)}</td>}
          <td>{totalPlanSales.toFixed(0)}</td>
          <td>{totalPlanHours.toFixed(0)}</td>
          <td
            className={classnames(css.efficiency, {
              [css.overTarget]: efficiency >= targetEfficiency,
            })}
          >
            {efficiency.toFixed(0)}
          </td>
          <td>
            {i18n.t('budgetPlanning.table.targetEfficiency')} {targetEfficiency}
          </td>
        </tr>
        {DayRows(
          dailyBudgetData,
          dailyForecastData,
          dailyPlanData,
          onAmountChange,
          editable,
          targetEfficiency,
          isStationPage,
          stationId,
          onNoteDialogOpen,
        )}
      </tbody>
    </table>
  )
}

export default BudgetTable
